import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type React from "react";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { threadDateDividers } from "@/hooks/dispatch/dispatchThread";
import { olderPageFloor, type ThreadEnds, threadEndChange } from "@/hooks/dispatch/threadPaging";
import { useDispatchThread } from "@/hooks/dispatch/useDispatchThread";
import { createLogger } from "@/logger";
import { ActionContext } from "@/models/ActionsProvider";
import { fetchFeedItemsAndDownloadEvents } from "@/models/actions/bootstrap";
import { fetchFeedItems, localFeedCache, shouldPageOlderOnOpen } from "@/models/actions/feedItems";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import { feedHeldThroughLogHead } from "@/models/feedItem/feedItemSync";
import type { PreferredLanguage } from "@/models/languages";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { runBootTask } from "@/store/bootRun";
import { idx, rowsByIndex, store } from "@/store/store";
import type { DriverRow } from "@/types/driver";
import { zinc } from "@/ui/tokens";
import { type DispatchPanelHandle, scrollByPage } from "../panelScroll";
import { ThreadDateDivider, ThreadEventPill, ThreadMessageBubble } from "./ThreadEntries";

/** Messages per history page of a thread. */
const THREAD_PAGE_SIZE = 100;
const log = createLogger("feed");

/** How many of the newest driver messages get a seen receipt when the thread shows them. */
const SEEN_RECEIPT_WINDOW = 50;

/** How close to the foot, in pixels, still counts as at the foot, so a new message keeps the view pinned there. */
const AT_FOOT_SLACK_PX = 4;

/**
 * A driver's message thread as the Dispatch page shows it: the channel's messages oldest first with the events between
 * them and a date divider opening each day. The composer that writes to the channel is the popover's, below the tabs,
 * so it stays put when the tab changes. This is the Dispatch page's own view of a channel, forked from the driver
 * list's so the two can evolve apart; underneath it reads the same store and sends through the same publish.
 *
 * Opening the thread does what opening the channel did: it pulls the newest page of history, the content of every
 * message on it the store has not fetched yet (the boot warmup fetches only the newest few per channel), and the
 * receipts on the latest messages, and it leaves a seen receipt on the driver's newest messages, so the read layer
 * keeps meaning what it meant. It never acknowledges: reading a channel is not handling it.
 *
 * Scrolling toward the top pages older history in, a page at a time, until the feed has none left; the view holds
 * still while a page lands above it.
 */
export default function DispatchThread({
  feedId,
  driver,
  workspaceId,
  now,
  handleRef,
}: {
  feedId: string;
  driver: DriverRow | null;
  workspaceId: string;
  /** A minute clock, for the acknowledgement lookback. */
  now: number;
  /** Receives the thread's {@link DispatchPanelHandle}, which pages its messages. */
  handleRef?: React.Ref<DispatchPanelHandle>;
}) {
  const { accountEvent } = useContext(ActionContext);
  const { myAccount } = useContext(MyAccountContext);
  const preferredLanguage = (myAccount?.preferredLanguage ?? "en") as PreferredLanguage;
  const entries = useDispatchThread(feedId, driver, now);
  const dividers = useMemo(() => threadDateDividers(entries), [entries]);

  // Paging state, per feed. The opening fetch counts as a page in flight, so scrolling cannot race it for the same page.
  const pageInFlightRef = useRef(false);
  /** The contiguity floor the last older page was requested from; a repeat for the same floor is skipped. */
  const requestedFloorRef = useRef<string | null>(null);
  const topInViewRef = useRef(false);
  const [pagingOlder, setPagingOlder] = useState(false);
  /** Bumped when a fetch moves the history, so a top marker still in view pages again. A failure never bumps it. */
  const [pageSettled, setPageSettled] = useState(0);
  /** Bumped on each feed change, so a page still in flight for an earlier open leaves the paging state alone. */
  const openTokenRef = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the paging state resets when the feed changes
  useEffect(() => {
    openTokenRef.current += 1;
    pageInFlightRef.current = false;
    requestedFloorRef.current = null;
    setPagingOlder(false);
  }, [feedId]);

  // Fill in the history, the content, and the receipts the store may not hold yet; the thread renders whatever is
  // there meanwhile. The warmup loads a channel's last two days of items without their text, audio, or files. A channel
  // it holds through the head needs no newest page: it pages back from its span's floor only when it holds less than a
  // page, and the content is downloaded here either way.
  useEffect(() => {
    let cancelled = false;
    let opening = true;
    pageInFlightRef.current = true;
    (async () => {
      try {
        const feed = store.get("feed", feedId);
        if (!feedHeldThroughLogHead(workspaceId, feed)) {
          await fetchFeedItems({ workspaceId, feedId, direction: "older", anchor: "tail", pageSize: THREAD_PAGE_SIZE });
        } else if (
          shouldPageOlderOnOpen({
            heldThroughHead: true,
            loadedFirstPage: feed?.loadedFirstPage ?? false,
            count: localFeedCache(feedId).count,
            pageSize: THREAD_PAGE_SIZE,
          })
        ) {
          await fetchFeedItems({
            workspaceId,
            feedId,
            direction: "older",
            anchor: "contiguity",
            pageSize: THREAD_PAGE_SIZE,
          });
        }
        if (cancelled) return;
        opening = false;
        pageInFlightRef.current = false;
        setPageSettled((count) => count + 1);
        const unhydrated = [...rowsByIndex("item", idx.itemsByFeed, feedId)]
          .filter((item) => item.loadedContent !== true && item.deletedAt == null)
          .map((item) => item.id);
        await Promise.all([
          downloadFeedItemContent(workspaceId, unhydrated, preferredLanguage),
          // A thread opened during boot counts toward the rail indicator while it loads; after boot it passes through.
          runBootTask("warming", "route.dispatchThread", fetchFeedItemsAndDownloadEvents(feedId, workspaceId)),
        ]);
      } catch (error) {
        log.warn("Thread history load failed; showing what the store holds", { feedId, error });
      } finally {
        if (!cancelled && opening) {
          pageInFlightRef.current = false;
          setPageSettled((count) => count + 1);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [feedId, workspaceId, preferredLanguage]);

  // One older page below the feed's contiguity floor, and its content. A page that failed or was skipped proved nothing:
  // its floor marker is dropped, and the retry waits for the marker to leave view and come back, as the feed list's
  // retry waits for the next scroll-end. Like the opening fetch, a page stops counting as in flight once its items land;
  // the content download runs after.
  const pageOlder = useCallback(async () => {
    // Nothing to do while a page is in flight, once the feed holds the start of its history, or for a floor requested.
    if (pageInFlightRef.current) return;
    const floor = olderPageFloor(store.get("feed", feedId), requestedFloorRef.current);
    if (floor === null) return;
    requestedFloorRef.current = floor;
    pageInFlightRef.current = true;
    setPagingOlder(true);
    const token = openTokenRef.current;
    let page: Awaited<ReturnType<typeof fetchFeedItems>> | undefined;
    try {
      page = await fetchFeedItems({
        workspaceId,
        feedId,
        direction: "older",
        anchor: "contiguity",
        pageSize: THREAD_PAGE_SIZE,
      });
    } catch (error) {
      log.warn("Older thread page failed; showing what the store holds", { feedId, error });
    }
    // A feed change mid-page already reset the paging state for the new open.
    if (openTokenRef.current !== token) return;
    pageInFlightRef.current = false;
    setPagingOlder(false);
    // A failed or skipped page moved no bound: drop the marker, and do not ask for the next page.
    if (!page || page.failed || page.skipped) {
      requestedFloorRef.current = null;
      return;
    }
    setPageSettled((count) => count + 1);
    // The page's items arrive without their text, audio, or files; fetch those the store does not hold yet.
    const unhydrated = page.items
      .map((item) => item.id)
      .filter((id) => {
        const row = store.get("item", id);
        return row !== undefined && row.loadedContent !== true && row.deletedAt == null;
      });
    try {
      await downloadFeedItemContent(workspaceId, unhydrated, preferredLanguage);
    } catch (error) {
      log.warn("Older thread page content failed; showing what the store holds", { feedId, error });
    }
  }, [feedId, workspaceId, preferredLanguage]);

  // Showing the driver's newest messages reads them, as scrolling the channel did. The action layer sends each seen
  // receipt once, so re-renders cost nothing.
  const messageIds = entries
    .filter((entry) => entry.kind === "message" && entry.fromDriver)
    .slice(-SEEN_RECEIPT_WINDOW)
    .map((entry) => entry.id)
    .join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the joined ids are the shown set's identity
  useEffect(() => {
    if (!accountEvent || !messageIds) return;
    for (const itemId of messageIds.split(",")) {
      if (store.get("item", itemId)?.hasRegisteredSeenEvent) continue;
      accountEvent("Saw Feed Item", { feedId, feedItemId: itemId });
    }
  }, [messageIds, feedId, accountEvent]);

  // A marker at the top of the scroller pages older history in about a viewport before the dispatcher reaches it.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const topMarkerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = scrollerRef.current;
    const marker = topMarkerRef.current;
    if (!scroller || !marker) return;
    const observer = new IntersectionObserver(
      ([sighting]) => {
        topInViewRef.current = sighting?.isIntersecting === true;
        if (topInViewRef.current) void pageOlder();
      },
      { root: scroller, rootMargin: "100% 0px 0px 0px" },
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [pageOlder]);

  // The observer reports only changes, so a marker still in view when a fetch settles pages again from here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a settled fetch is the trigger
  useEffect(() => {
    if (topInViewRef.current) void pageOlder();
  }, [pageSettled, pageOlder]);

  // The newest message is what the dispatcher came for: land at the foot on open, and stay there as messages arrive
  // while the dispatcher is there. Scrolled up, a page landing above holds the view where it is, by its distance from
  // the foot, and a message arriving below leaves it alone. Prepend and append are told apart by the thread's end ids.
  const footDistanceRef = useRef(0);
  const atFootRef = useRef(true);
  const endsRef = useRef<ThreadEnds | null>(null);
  const recordPosition = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    footDistanceRef.current = scroller.scrollHeight - scroller.scrollTop;
    atFootRef.current = footDistanceRef.current - scroller.clientHeight <= AT_FOOT_SLACK_PX;
  }, []);
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const oldest = entries[0]?.id;
    const newest = entries[entries.length - 1]?.id;
    const after = oldest && newest ? { feedId, oldest, newest } : null;
    const change = threadEndChange(endsRef.current, after);
    endsRef.current = after;
    if (!scroller) return;
    // Opening, or already at the foot: land on (or stay at) the newest entry.
    if (change === "opened" || atFootRef.current) {
      scroller.scrollTop = scroller.scrollHeight;
    } else if (change !== "appended") {
      // A page or the spinner above, or content filling in: hold the view by its distance from the foot.
      scroller.scrollTop = scroller.scrollHeight - footDistanceRef.current;
    }
    recordPosition();
  }, [entries, pagingOlder, feedId, recordPosition]);

  useImperativeHandle(
    handleRef,
    () => ({ scrollPage: (direction) => scrollByPage(scrollerRef.current, direction) }),
    [],
  );

  return (
    <Box
      data-sb={TestIds.dispatch.thread}
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: zinc[900] }}
    >
      <Box
        ref={scrollerRef}
        onScroll={recordPosition}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowAnchor: "none", py: 1.5 }}
      >
        <Box ref={topMarkerRef} sx={{ height: "1px" }} />
        {pagingOlder && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={16} sx={{ color: zinc[500] }} />
          </Box>
        )}
        {entries.length === 0 ? (
          <Typography sx={{ px: 2, py: 4, textAlign: "center", color: zinc[500], fontSize: "0.875rem" }}>
            No messages on this channel yet.
          </Typography>
        ) : (
          entries.map((entry, index) => (
            <Fragment key={entry.id}>
              {dividers[index] && <ThreadDateDivider label={dividers[index]} />}
              {entry.kind === "message" ? <ThreadMessageBubble message={entry} /> : <ThreadEventPill event={entry} />}
            </Fragment>
          ))
        )}
      </Box>
    </Box>
  );
}
